// The page's own model of the user whose profile this is, and the only reader
// of window.$viewedUser anywhere under profiles/autotest. The global is a
// Backbone-era User instance the profile template builds inline, before any
// bundle runs; arms/ reads the profile through this file, never off the global.
//
// It also holds the autotest's ONLY two DOM selectors outside surface.ts, both
// in profileGaps: .js-profile-add-about-prompt and .js-UserInfo-social-cv.
// They are read-only presence probes — the page renders each one exactly when a
// piece of the profile is missing, which is a fact about the user and not about
// the layout, so asking the DOM is asking the cheapest source. Anything that
// STRUCTURES or MUTATES the page belongs in surface.ts, without exception.
//
// arms/ holds no selector for page markup: neither class above, nor any page
// class surface.ts names. An arm may still query inside markup it built itself
// — that is the arm's own DOM, not the page's.

import { currentUser } from 'js/utils/user';

import { DEADLINE_MS } from './pageData';

const ADD_ABOUT_PROMPT = '.js-profile-add-about-prompt';
const CV_LINK = '.js-UserInfo-social-cv';

// A jQuery Deferred, which is thenable but is not a Promise: this is the only
// shape of it we depend on, and .done is the only callback User#load_works_count
// ever fires.
type WorksCountDeferred = {
  done: (callback: (count: number) => void) => unknown;
};

type Department = {
  id?: number | null;
  name?: string | null;
  university?: { id?: number | null; name?: string | null } | null;
};

export type Interest = { id?: unknown; name?: unknown };

// ViewedUserSerializer, which is ProfileSerializer plus is_analytics_public,
// which is UserSerializer plus photo/has_photo/department — with two things
// bolted on client-side that no serializer will show you: `interests`, assigned
// by the profile template from a second serializer pass, and the User prototype
// itself, which is where load_works_count comes from.
//
// The required keys are the ones User#set_attrs throws without, so a
// $viewedUser that exists at all has them. department is emitted only for an
// affiliated profile and email only for a viewer who is not the owner.
export type ViewedUser = {
  id: number;
  first_name: string;
  last_name: string;
  page_name: string;
  domain_name: string;
  display_name?: string;
  url?: string;
  created_at?: string;
  middle_initials?: string | null;
  email?: string;
  photo?: string;
  has_photo?: boolean;
  department?: Department | null;
  is_analytics_public?: boolean;
  interests?: Interest[];
  load_works_count?: () => WorksCountDeferred;
};

// True means MISSING. bio and cv are the two DOM probes; the other two are the
// serializer, which carries no equivalent for either of them.
export type ProfileGaps = {
  bio: boolean;
  affiliation: boolean;
  cv: boolean;
  photo: boolean;
};

export function viewedUser(): ViewedUser | null {
  return (
    (window as unknown as { $viewedUser?: ViewedUser }).$viewedUser ?? null
  );
}

export function viewedUserId(): number | null {
  return viewedUser()?.id ?? null;
}

export function viewedPageName(): string | null {
  return viewedUser()?.page_name ?? null;
}

// The premium flag control's own gates branch on: the Navbar routes its
// Readers item to the analytics dashboard or to the upgrade page off this same
// module's currentUser. Anything short of an explicit true - no $current_user
// on the page, or a payload with no flag - reads as free, so a destination
// gated on this can never skip the upsell interstitial.
export function viewerIsPremium(): boolean {
  return currentUser?.premium === true;
}

// The rendered works count is the LOADED count — twenty for a five-hundred work
// author — so the real figure has to come from the model's own loader. That
// loader hands back a fresh Deferred and wires only .done onto the underlying
// JSONP call, no .fail, so a dead request leaves it neither resolved nor
// rejected: it simply never settles. The deadline is the whole reason this is
// safe to await, and null is a truthful unknown because the true corpus size is
// rendered nowhere on the page to fall back to.
export function worksCount(): Promise<number | null> {
  const user = viewedUser();
  const loader = user?.load_works_count;
  if (typeof loader !== 'function') return Promise.resolve(null);

  return new Promise((resolve) => {
    // pageData's deadline, imported rather than restated: this deferred can
    // outlive any useful render exactly as a dead fetch can.
    const deadline = window.setTimeout(() => resolve(null), DEADLINE_MS);

    try {
      // .call, not loader(): load_works_count reaches through `this` for both
      // the user id it puts in the URL and the per-attribute promise cache it
      // memoizes on, so an unbound call either throws on undefined or quietly
      // requests user_id=undefined. Both end at the deadline with null, which
      // is exactly the failure that looks like a slow endpoint.
      //
      // That memoization also means a second caller's .done can fire
      // synchronously, inside this call rather than after it — arming the
      // deadline first is what stops that clearing a timer that does not exist.
      loader.call(user).done((count: number) => {
        window.clearTimeout(deadline);
        resolve(Number.isFinite(count) ? count : null);
      });
    } catch {
      window.clearTimeout(deadline);
      resolve(null);
    }
  });
}

// The user's own interests: the profile template serializes
// Profile#research_interests, which comes through interest_followings ordered
// by display_order, and that order is the one the user set. Not filtered for
// blocked interests — good_research_interests does that and this path does not
// go through it — so an arm putting these names in copy is quoting the user.
//
// The limit window is positional: the first `limit` entries are considered and
// the unusable ones dropped, never backfilled from further down the list —
// which is what keeps topInterest quoting the user's actual first choice
// rather than the first one that happened to survive.
export function interests(limit = 5): Array<{ id: number; name: string }> {
  const list = viewedUser()?.interests ?? [];

  return list.slice(0, limit).flatMap((interest) => {
    const id = Number(interest?.id);
    const name = typeof interest?.name === 'string' ? interest.name.trim() : '';

    return !Number.isFinite(id) || id <= 0 || name === '' ? [] : [{ id, name }];
  });
}

// The first-ranked interest alone: interests' positional window, at one.
export function topInterest(): { id: number; name: string } | null {
  return interests(1)[0] ?? null;
}

export function profileGaps(): ProfileGaps {
  const user = viewedUser();

  return {
    // The page's own definition of a missing biography: the prompt renders for
    // a self-viewing owner exactly when about, supervisors, phone and address
    // are all blank. It is owner-only markup, so on a visitor pageview this
    // reads false for every profile, missing bio or not.
    bio: document.querySelector(ADD_ABOUT_PROMPT) != null,
    // The serializer emits the key only for an affiliated profile.
    affiliation: user?.department == null,
    // The CV link renders for any viewer, whenever the profile page has an
    // attachment — but it lives inside an external-links block, so its absence
    // is "no CV" and never "no room for one".
    cv: document.querySelector(CV_LINK) == null,
    photo: user?.has_photo !== true,
  };
}
